import React from "react";
import {
  LocalizationProvider,
  DatePicker as MuiDatePicker,
} from "@mui/x-date-pickers";
import { Box, useTheme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import "dayjs/locale/en-gb";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface DatePickerProps {
  label: string;
  value: Date | Dayjs | null;
  onChange: (newValue: Date | null) => void;
  error?: boolean;
  disabled?: boolean;
  borderColor?: string;
  backgroundColor?: string;
  fontSize?: string;
  require?: boolean;
  /** Use when DatePicker is inside a Dialog/Modal so the calendar appears above it */
  popperZIndex?: number;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  error = false,
  disabled = false,
  borderColor = "#8B8B8B",
  backgroundColor = "#FFF",
  fontSize = "13px",
  require = false,
  popperZIndex,
}) => {
  const isError = error && require && !value;
  const theme = useTheme();
  return (
    <Box sx={{ flex: "1 1 100%" }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <MuiDatePicker
          label={label}
          disabled={disabled}
          slotProps={
            popperZIndex != null
              ? { popper: { sx: { zIndex: popperZIndex } } }
              : undefined
          }
          sx={{
            width: "100%",
            background: backgroundColor,
            "& .MuiInputBase-root": {
              height: "37px",
              width: "inherit",
            },
            "& .MuiOutlinedInput-root": {
              width: "100%",
              fontSize,
              "& fieldset": {
                borderColor: isError
                  ? theme.palette.customColors.borderErrorRedColor
                  : disabled
                    ? theme.palette.customColors.disabledBorderColor
                    : borderColor,
              },
              "&:hover fieldset": {
                borderColor: isError
                  ? theme.palette.customColors.borderErrorRedColor
                  : theme.palette.customColors.purple,
              },
              "&.Mui-focused fieldset": {
                borderColor: isError
                  ? theme.palette.customColors.borderErrorRedColor
                  : theme.palette.customColors.purple,
                borderWidth: "1px",
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: fontSize,
              top: "50%",
              transform: "translateY(-50%)",
              left: 10,
              color: disabled
                ? theme.palette.customColors.disabledBorderColor
                : theme.palette.customColors.tableGrayHeadingColor,
              "&.Mui-focused": {
                color: isError
                  ? theme.palette.customColors.borderErrorRedColor
                  : theme.palette.customColors.purple,
              },
            },
            "& .MuiInputLabel-shrink": {
              top: "-16px",
              transform: "translateY(0)",
            },
            "& .MuiInputBase-input": {
              color: disabled
                ? theme.palette.customColors.disabledBorderColor
                : theme.palette.customColors.darkBlackBorder,
              fontSize,
            },
            "& .MuiFormHelperText-root": {
              color: theme.palette.customColors.borderErrorRedColor,
              fontSize: "12px",
              marginTop: "4px",
            },
          }}
          onChange={(newValue) => {
            onChange(newValue ? newValue.toDate() : null);
          }}
          value={value ? dayjs(value) : null}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DatePicker;
